/** @jsxImportSource @emotion/react */

import {
  EuiCheckbox,
  EuiDataGrid,
  EuiDataGridColumn,
  EuiDataGridControlColumn,
  EuiDataGridSchemaDetector,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiPanel,
  useEuiTheme,
} from "@inscopix/ideas-eui";
import { useSelectionState } from "components/FlyoutFiles/useSelectionState";
import { UserAvatarRenderer } from "components/UserAvatarRenderer/UserAvatarRenderer";
import { filesize } from "utils/filesize";
import { ApplicationUser, File as DrsFile } from "graphql/_Types";
import { orderBy } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { FileSource, FileStatus } from "types/constants";
import { isDefined } from "utils/isDefined";
import { FileBrowserActionControls } from "./FileBrowserActionControls";
import { css, Interpolation, Theme } from "@emotion/react";
import { htmlIdGenerator } from "@inscopix/ideas-eui";
import { RequireAllOrNone } from "type-fest";
import assert from "assert";
import {
  FileBrowserFilterOptions,
  FileBrowserSearchAndFilter,
  FileBrowserSearchAndFilterProps,
} from "components/FileBrowser/FileBrowserSearchAndFilter";
import { formatDate } from "utils/formatDate";
import { FileBrowserCellRendererFileType } from "./FileBrowserCellRendererFileType";
import { ModalProvider } from "providers/ModalProvider/ModalProvider";
import { ButtonProjectUpload } from "components/ButtonProjectUpload/ButtonProjectUpload";
import { getTagsList } from "utils/getTagsList";

type TFile = Pick<
  DrsFile,
  | "id"
  | "name"
  | "fileType"
  | "fileFormat"
  | "fileStructure"
  | "status"
  | "dateCreated"
  | "uploadStatus"
  | "projectId"
  | "size"
  | "seriesParentId"
  | "processingStatus"
  | "fileFormat"
  | "originalName"
  | "tags"
>;

export type FileBrowserFile = TFile & {
  source: FileSource;
  user: Pick<ApplicationUser, "id" | "firstName" | "lastName">;
} & (
    | { isSeries: true; seriesFiles: TFile[] }
    | { isSeries: false; seriesFiles: null }
  );

const schemaDetectors = [
  {
    type: "size" as const,
    comparator: () => 0 as const,
    detector: () => 0 as const,
    sortTextAsc: "Small-Large",
    sortTextDesc: "Large-Small",
    icon: "tokenFile",
  },
  {
    type: "text" as const,
    comparator: () => 0 as const,
    detector: () => 0 as const,
    sortTextAsc: "A-Z",
    sortTextDesc: "Z-A",
    icon: "tokenText",
  },
  {
    type: "source" as const,
    comparator: () => 0 as const,
    detector: () => 0 as const,
    sortTextAsc: "Analysis Result - Uploaded",
    sortTextDesc: "Uploaded - Analysis Result",
    icon: "tokenText",
  },
] satisfies EuiDataGridSchemaDetector[];

type AvailableSchema =
  | "datetime"
  | "boolean"
  | (typeof schemaDetectors)[number]["type"];

export type FileBrowserColumn<T extends string> = Omit<
  EuiDataGridColumn,
  | "id"
  // disable column level details specification in favor of custom column props
  // that allow us to dynamically determine and render detail views via callbacks
  | "isDetails"
> & {
  // note: if you add a new column id, you must decide if it should be searchable or not
  // if it's not searchable, disable it by adding it to unSearchableColumnIds
  id:
    | T
    | Extract<
        keyof FileBrowserFile,
        "name" | "dateCreated" | "status" | "tags" | "isSeries"
      >
    | Extract<keyof FileBrowserFile, "user" | "size">;
  schema: AvailableSchema;
  defaultVisibility?: "hidden";
};

// can't be stored on columns because may have dependencies
// that cause column reference to change and break sorting
export type FileBrowserColumnProps<T extends FileBrowserFile> =
  RequireAllOrNone<
    {
      cellRenderer: (props: { file: T }) => React.ReactNode;
      sortValueGetter: (file: T) => string | number | null | bigint;
      isDetails: (file: T) => boolean;
      detailRenderer: (file: T) => NonNullable<React.ReactNode>;
    },
    "detailRenderer" | "isDetails"
  >;

interface FileRendererProps<T extends FileBrowserFile> {
  file: T;
}

type TAction<T extends FileBrowserFile> = {
  // Actions return a Promise that resolve with a list of successful files
  // when the action completes. This is necessary for the browser to manage
  // selection state and deselect files when actions complete
  onExecute: (files: T[]) => Promise<{
    successfulFiles: Pick<T, "id">[];
    errors: Error[];
  }>;
  isDisabled?: (files: T[]) => boolean;
  disabledToolTip?: ((files: T[]) => string | undefined) | string;
  suppressLoading?: boolean | ((files: T[]) => boolean);
};

const styles = {
  toolbarText: css`
    font-size: 12px;
    font-weight: 600;
    padding: 0px 8px;
    font-family:
      -apple-system, BlinkMacSystemFont, "Segoe UI", "Oxygen", "Ubuntu",
      "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif,
      "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  `,
};

export type FileBrowserActionName =
  | "archive"
  | "delete"
  | "cancel_upload"
  | "download"
  | "unassign"
  | "unarchive"
  | "createSeries"
  | "rename"
  | "revertSeries";

export type FileBrowserActions<T extends FileBrowserFile> = {
  [key in FileBrowserActionName]?: TAction<T>;
};

export type FileBrowserProps<
  T extends FileBrowserFile,
  ExtraColId extends string | never = never,
> = RequireAllOrNone<
  {
    files: T[];
    fileRenderer: (props: FileRendererProps<T>) => React.ReactNode;
    selection?: {
      actions: FileBrowserActions<T>;
    };
    defaultSource: FileBrowserSearchAndFilterProps["selectedOptions"]["source"];
    css?: Interpolation<Theme>;
    defaultHiddenColumns?: (FileBrowserColumn<ExtraColId>["id"] | ExtraColId)[];
    extraColumns?: {
      id: ExtraColId;
      isSearchable: boolean;
      schema: AvailableSchema;
      displayAsText: string;
    }[];
    extraColumnProps?: Record<ExtraColId, FileBrowserColumnProps<T>>;
    defaultColumnOrder?: FileBrowserColumn<ExtraColId>["id"][];
  },
  "extraColumns" | "extraColumnProps"
>;

/**
 * ********************************************************
 * IMPORTANT NOTE TO ANYONE WORKING ON THIS IN THE FUTURE
` * IF THIS NEEDS TO ACCESS INFORMATION IN DATASET, PROJECT
 * OR OTHER CONTEXTS, PLEASE PASS INFO IN AS PROPS SUCH THAT
 * THIS CAN ALWAYS DISPLAY A LIST OF FILES
 * ********************************************************
 */

export const FileBrowser = <
  T extends FileBrowserFile,
  ExtraColId extends string,
>({
  files,
  fileRenderer,
  selection,
  defaultSource,
  css: cssFromProps,
  defaultHiddenColumns,
  extraColumns,
  extraColumnProps,
  defaultColumnOrder,
}: FileBrowserProps<T, ExtraColId>) => {
  // some columns don't make sense to be searchable (e.g. boolean columns)
  const unSearchableColumnIds: ReadonlySet<
    FileBrowserColumn<ExtraColId>["id"]
  > = useMemo(
    () =>
      new Set([
        "isSeries",
        "size" as const,
        ...(extraColumns ?? [])
          .map(({ id, isSearchable }) => (!isSearchable ? id : undefined))
          .filter(isDefined),
      ]),
    [extraColumns],
  );

  const fileBrowserValueGettersByColId = useMemo(() => {
    const valueGetters: Record<
      FileBrowserColumn<ExtraColId>["id"],
      FileBrowserColumnProps<T>["sortValueGetter"]
    > = {
      name: (file) => file.name,
      dateCreated: (file) => file.dateCreated,
      status: (file) => FileStatus[file.status],
      user: (file) => file.user.firstName + " " + file.user.lastName,
      tags: (file) => {
        const fileType =
          file.fileType !== null && file.fileType !== 1 ? file.fileType : null;
        return getTagsList(file.tags, fileType === null ? null : [fileType]);
      },
      size: (file) => BigInt(file.size ?? 0),
      isSeries: (file) => (file.isSeries ? 1 : 0),
      ...Object.entries(extraColumnProps ?? {}).reduce(
        (map, [key, val]) => {
          map[key as ExtraColId] = (
            val as FileBrowserColumnProps<T>
          ).sortValueGetter;
          return map;
        },
        {} as Record<ExtraColId, FileBrowserColumnProps<T>["sortValueGetter"]>,
      ),
    };
    return valueGetters;
  }, [extraColumnProps]);

  const { euiTheme } = useEuiTheme();

  // Search and filter
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilterOptions, setSelectedFilterOptions] = useState<
    FileBrowserSearchAndFilterProps["selectedOptions"]
  >({
    source: defaultSource,
  });

  // This needs to be persisted as a reference between renders for column reordering
  // Intentionally made a useState vs useMemo because a useMemo with dependencies
  // would cause a reference change that would break column ordering
  const [columns] = useState(() => {
    const defaultColDef: Pick<EuiDataGridColumn, "isExpandable"> = {
      isExpandable: false,
    };
    const defaultColumns: FileBrowserColumn<ExtraColId>[] = [
      {
        id: "name",
        displayAsText: "Name",
        schema: "text",
      },
      {
        id: "dateCreated",
        schema: "datetime",
        displayAsText: "Date",
        defaultSortDirection: "desc",
      },
      {
        id: "status",
        displayAsText: "Status",
        schema: "text",
        defaultVisibility: "hidden",
      },
      {
        id: "user",
        displayAsText: "User",
        schema: "text",
        initialWidth: 60,
      },
      {
        id: "tags",
        displayAsText: "Tags",
        schema: "text",
      },
      {
        id: "size",
        displayAsText: "Size",
        schema: "size",
      },
      {
        id: "isSeries",
        displayAsText: "Series",
        schema: "boolean",
        initialWidth: 75,
        defaultVisibility: "hidden",
      },
    ];
    const allColumns: FileBrowserColumn<ExtraColId>[] = [
      ...defaultColumns,
      ...(extraColumns ?? []).map((col) => ({
        ...col,
      })),
    ].map((col) => ({
      ...defaultColDef,
      ...col,
    }));

    const colOrderByColId = (defaultColumnOrder ?? []).reduce(
      (acc, curr, i) => {
        acc[curr] = i;
        return acc;
      },
      {} as Record<
        NonNullable<typeof defaultColumnOrder>[number],
        number | undefined
      >,
    );

    if (defaultColumnOrder !== undefined) {
      return allColumns.sort((a, b) => {
        return (colOrderByColId[a.id] ?? -1) - (colOrderByColId[b.id] ?? -1);
      });
    }
    return allColumns;
  });

  // see above note about columns array reference needing to be static
  // any dynamic column props need to go here to avoid breaking column ordering
  const columnPropsByColumnId = useMemo(() => {
    const colProps: Record<
      FileBrowserColumn<ExtraColId>["id"],
      FileBrowserColumnProps<T>
    > = {
      name: {
        cellRenderer: ({ file }) => fileRenderer({ file }),
        sortValueGetter: fileBrowserValueGettersByColId["name"],
      },
      dateCreated: {
        cellRenderer: ({ file }) => formatDate(file.dateCreated),
        sortValueGetter: fileBrowserValueGettersByColId["dateCreated"],
      },
      status: {
        cellRenderer: ({ file }) => <div>{FileStatus[file.status]}</div>,
        sortValueGetter: fileBrowserValueGettersByColId["status"],
      },
      user: {
        cellRenderer: ({ file }) => (
          <UserAvatarRenderer size={"s"} userId={file.user.id} />
        ),
        sortValueGetter: fileBrowserValueGettersByColId["user"],
      },
      tags: {
        cellRenderer: FileBrowserCellRendererFileType,
        sortValueGetter: fileBrowserValueGettersByColId["tags"],
      },
      size: {
        cellRenderer: ({ file }) => filesize(file.size),
        sortValueGetter: fileBrowserValueGettersByColId["size"],
      },
      isSeries: {
        cellRenderer: ({ file }) => {
          const isChecked = file.isSeries;
          return isChecked ? (
            <EuiIcon aria-label="checkmark" type="check" />
          ) : null;
        },
        sortValueGetter: fileBrowserValueGettersByColId["isSeries"],
      },

      ...(extraColumnProps ??
        ({} as Record<ExtraColId, FileBrowserColumnProps<T>>)),
    };

    return colProps;
  }, [extraColumnProps, fileBrowserValueGettersByColId, fileRenderer]);

  /**
   * Column visibility
   */

  const [visibleColumns, setVisibleColumns] = useState<
    FileBrowserColumn<ExtraColId>["id"][]
  >(
    columns
      .filter(({ defaultVisibility }) => defaultVisibility !== "hidden")
      .filter(({ id }) => !defaultHiddenColumns?.includes(id))
      .map(({ id }) => id),
  );

  /**
   * Column sorting
   */

  const [sortingColumns, setSortingColumns] = useState<
    { id: FileBrowserColumn<ExtraColId>["id"]; direction: "asc" | "desc" }[]
  >([]);

  const data = useMemo(() => {
    const { sortValueGetters, sortDirections } = sortingColumns.reduce<{
      sortValueGetters: FileBrowserColumnProps<T>["sortValueGetter"][];
      sortDirections: ("asc" | "desc")[];
    }>(
      (sortData, { id: columnId, direction }) => {
        const columnProps = columnPropsByColumnId[columnId];
        sortData.sortValueGetters.push(columnProps.sortValueGetter);
        sortData.sortDirections.push(direction);
        return sortData;
      },
      { sortValueGetters: [], sortDirections: [] },
    );

    const applyFileBrowserSearchAndFilter: (
      files: T[],
      selectedOptions: FileBrowserFilterOptions,
      query: string,
    ) => T[] = (files, selectedOption, query) => {
      // list of searchable columns
      const fileBrowserSearchableColumnIds: FileBrowserColumn<ExtraColId>["id"][] =
        (
          Object.keys(fileBrowserValueGettersByColId) as Array<
            keyof typeof fileBrowserValueGettersByColId
          >
        ).filter((colId) => !unSearchableColumnIds.has(colId));

      const { source } = selectedOption;
      return files
        .filter((file) => {
          switch (source) {
            case "ALL":
              return true;
            case "ANALYSIS_RESULTS":
              return file.source === FileSource.ANALYSIS_RESULT;
            case "UPLOADED":
              return file.source === FileSource.UPLOADED;
            default:
              return false;
          }
        })
        .filter((file) => {
          if (query === "") {
            return true;
          }
          return fileBrowserSearchableColumnIds.some((colId) => {
            const valueGetter = fileBrowserValueGettersByColId[colId];
            const value = valueGetter(file);
            const isFieldMatch = (value ?? "")
              .toString()
              .toLocaleLowerCase()
              .includes(query.toLocaleLowerCase());

            return isFieldMatch;
          });
        });
    };

    const filteredFiles = applyFileBrowserSearchAndFilter(
      files,
      selectedFilterOptions,
      searchQuery,
    );

    const sortedData = orderBy(
      // pre order by date so we don't have a confusing default sort applied
      orderBy([...filteredFiles], [(file) => file.dateCreated], ["desc"]),
      sortValueGetters,
      sortDirections,
    );
    return sortedData;
  }, [
    columnPropsByColumnId,
    fileBrowserValueGettersByColId,
    files,
    searchQuery,
    selectedFilterOptions,
    sortingColumns,
    unSearchableColumnIds,
  ]);

  /**
   * Selection state
   */

  const [selectedFiles, selectedFileActions] = useSelectionState(data);

  const leadingControlColumns = useMemo(() => {
    const cols: EuiDataGridControlColumn[] = [];
    if (isDefined(selection) && Object.keys(selection).length > 0) {
      cols.push({
        id: "selection",
        width: 32,
        headerCellRender: () => {
          const isIndeterminate =
            selectedFiles.size > 0 && selectedFiles.size < data.length;
          return (
            <EuiCheckbox
              id="selection-toggle"
              aria-label="Select all rows"
              indeterminate={isIndeterminate}
              checked={selectedFiles.size > 0}
              onChange={(e) => {
                if (isIndeterminate) {
                  // clear selection
                  selectedFileActions.deselectAll();
                } else {
                  if (e.target.checked) {
                    selectedFileActions.selectAll();
                  } else {
                    selectedFileActions.deselectAll();
                  }
                }
              }}
            />
          );
        },
        rowCellRender: ({ rowIndex }) => {
          const file = data[rowIndex];
          const isChecked = selectedFiles.has(file.id);
          return (
            <div>
              <EuiCheckbox
                id={htmlIdGenerator()()}
                aria-label={`Select row ${rowIndex}, ${data[rowIndex].name}`}
                checked={isChecked}
                onClick={(e) => {
                  if (e.currentTarget.checked) {
                    selectedFileActions.selectItems([file], e.shiftKey);
                  } else {
                    selectedFileActions.deselectItems([file]);
                  }
                }}
                onChange={() => undefined}
              />
            </div>
          );
        },
      });
      return cols;
    }
  }, [data, selectedFileActions, selectedFiles, selection]);

  return (
    <div
      css={[
        {
          display: "flex",
          minWidth: 0,
          minHeight: 0,
          flex: 1,
        },
        cssFromProps,
      ]}
    >
      <EuiFlexGroup
        direction="column"
        css={{ minWidth: 515, flex: 1, gap: euiTheme.size.s }}
      >
        <EuiFlexGroup alignItems="center" responsive={false}>
          <FileBrowserSearchAndFilter
            query={searchQuery}
            onChangeQuery={setSearchQuery}
            selectedOptions={selectedFilterOptions}
            onChangeSelectedOptions={setSelectedFilterOptions}
          />
          <EuiFlexItem grow={false}>
            <ButtonProjectUpload />
          </EuiFlexItem>
        </EuiFlexGroup>

        <EuiPanel
          hasBorder
          paddingSize="none"
          style={{
            width: "100%",
            overflow: "hidden",
            minWidth: 0,
            marginTop: 0,
          }}
        >
          <ModalProvider>
            <EuiDataGrid
              title=""
              aria-label="File Browser"
              className="file-browser-data-grid"
              columns={columns}
              schemaDetectors={schemaDetectors}
              columnVisibility={{
                visibleColumns: visibleColumns,
                setVisibleColumns: (columnIds) =>
                  setVisibleColumns(
                    columnIds as (typeof columns)[number]["id"][],
                  ),
              }}
              rowCount={data.length}
              gridStyle={{
                border: "horizontal",
                header: "underline",
                cellPadding: "l",
              }}
              sorting={{
                columns: sortingColumns,
                onSort: (columns) =>
                  setSortingColumns(columns as typeof sortingColumns),
              }}
              renderCellValue={function RenderCellValue({
                rowIndex,
                columnId,
                setCellProps,
              }) {
                const colId: FileBrowserColumn<ExtraColId>["id"] =
                  columnId as FileBrowserColumn<ExtraColId>["id"];
                const file = data[rowIndex];
                const colProps = columnPropsByColumnId[colId];

                useEffect(() => {
                  if (
                    isDefined(colProps.isDetails) &&
                    colProps.isDetails(file)
                  ) {
                    setCellProps({ isExpandable: true });
                  } else {
                    setCellProps({ isExpandable: false });
                  }
                }, [columnId, setCellProps, colProps, file]);

                return colProps.cellRenderer({ file });
              }}
              renderCellPopover={({ rowIndex, columnId }) => {
                const colId: FileBrowserColumn<ExtraColId>["id"] =
                  columnId as FileBrowserColumn<ExtraColId>["id"];
                const file = data[rowIndex];
                const colProps = columnPropsByColumnId[colId];
                assert(
                  isDefined(colProps.detailRenderer),
                  "Expected detail renderer to be defined in col def",
                );
                return colProps.detailRenderer(file);
              }}
              toolbarVisibility={{
                showFullScreenSelector: false,
                showDisplaySelector: false,
                additionalControls: {
                  left:
                    selectedFiles.size > 0 ? (
                      <span key="selected file count" css={styles.toolbarText}>
                        {selectedFiles.size} file(s) selected
                      </span>
                    ) : undefined,
                  right: (
                    <>
                      {selection?.actions !== undefined && (
                        <FileBrowserActionControls<T>
                          actions={selection?.actions}
                          selectedFiles={Array.from(selectedFiles.values())}
                          selectedFileActions={selectedFileActions}
                        />
                      )}
                    </>
                  ),
                },
              }}
              leadingControlColumns={leadingControlColumns}
            />
          </ModalProvider>
        </EuiPanel>
      </EuiFlexGroup>
    </div>
  );
};
